import * as PIXI from 'pixi.js';
import { int2rgb } from '../../../libs/videoData';
import { notifyBugsnag } from '../../../libs/other';
import { Config } from '../../../video-data/config';
import type {
    CharTuple,
    NodeAndOffset
} from './CanvasTextEditingUtils';
import {
    sanitizeText,
    getRootContainerScale,
    getSelectionFromCharRange,
    getUpdatedHighlightRanges,
    getCharRangeFromSelection,
    getContentFromInput,
    restoreCursor,
    getSelectionForVerticalTraverse,
    GroupedTextError
} from './CanvasTextEditingUtils';
import {
    buildInputContent,
    createTextInput,
    createTextPlaceholder
} from './CanvasTextElementBuilder';
import type { CanvasTextAsset } from './CanvasTextAsset';

/**
 * Natural Text Editing module for a TextAsset. Contains all properties and logic needed to expose
 * an HTML element on the Web side so a user can make WYSIWYG changes to the TextAsset text property
 */

type InputEventData = {
    originalText: string;
    newData: string;
    updatedText: string;
    netCharDiff: number;
    prevCharRange: CharTuple;
    eventType: string;
    newTextSize: number;
};
export class CanvasTextEditingManager {
    textAsset: CanvasTextAsset = null;
    readonly #domContainer: HTMLElement = null;
    #rootContainer: HTMLDivElement = null;
    #textInput: HTMLDivElement = null;
    #inputEventData: InputEventData = {
        originalText: null,
        updatedText: null,
        newData: null,
        netCharDiff: null,
        prevCharRange: null,
        eventType: null,
        newTextSize: null,
    };

    #editingEmptyState = false;

    #inOverflowState: boolean = false;

    #isBoundsUpdatedForBuild = false;

    _eventListenersToCleanup: Array<[HTMLElement|Window, string, (e: Event) => void]> = [];

    #destroyed = false;

    get destroyed() {
        return this.#destroyed;
    }

    static rootContainerId = 'luminary-nte-container';
    static contentEditableId = 'luminary-input';
    static wordElementClass = 'word-element';
    static placeholderId = 'luminary-text-placeholder';
    static textLineClass = 'text-line';
    static NTE_EVENT_EDITING_EXCEPTION = 'nte-editing-exception';
    static NTE_EVENT_TEXT_OVERFLOW = 'nte-text-overflow';

    #onDomResizedResolve: Function;
    #onDomResized: Promise<void>;
    #resizeObserver: ResizeObserver;
    #onResizeCallbacks: Function[] = [];

    /**
     * The TextAsset instance that manages the TextEditingManager must be passed in to provide the input
     * access to its members
     */
    constructor(textAsset: CanvasTextAsset, domContainer: HTMLElement) {
        if (!Config.isCanvasTextAsset(textAsset)) {
            throw new Error('A valid TextAsset instance must be supplied when creating a TextEditingManager.');
        }
        this.textAsset = textAsset;

        this.#domContainer = domContainer;
        this.#rootContainer = this._constructRootContainer();
        this.#textInput = this.rootContainer.querySelector(`#${CanvasTextEditingManager.contentEditableId}`);
    }

    _build() {
        if (this.destroyed) {
            return;
        }
        if (!this.textAsset.isLoaded) {
            return;
        }

        this.#isBoundsUpdatedForBuild = false;

        // Styles are dependent on content. Build contents first
        this._buildContents();
        this._updateStyles();
        this.resetCalculatedState();
    }

    /**
     * If the text input is showing a special UI for when the text asset is empty
     */
    get isShowingEmptyState(): boolean {
        return this.#editingEmptyState;
    }

    get placeholderContainer(): HTMLDivElement|null {
        if (!this.isShowingEmptyState) {
            return null;
        }
        return this.rootContainer.querySelector(`#${CanvasTextEditingManager.placeholderId}`);
    }

    /**
     * The externally provided element from the DOM which the TextEditingManager should add
     * all text editing nodes to.
     */
    get domContainer(): HTMLElement {
        return this.#domContainer;
    }

    /**
     * The top level HMTL Node for this text editing input
     */
    get rootContainer(): HTMLDivElement {
        return this.#rootContainer;
    }

    /**
     * The contenteditable node
     */
    get textInput(): HTMLDivElement {
        return this.#textInput;
    }

    get inOverflowState() {
        return this.#inOverflowState;
    }

    set inOverflowState(isOverflowed: boolean) {
        const prevOverflowState = this.#inOverflowState;
        this.#inOverflowState = isOverflowed;

        if (isOverflowed) {
            // regardless of previous value, fire a web event if text is overflowed
            const webEvent = new Event(CanvasTextEditingManager.NTE_EVENT_TEXT_OVERFLOW);
            this.rootContainer.dispatchEvent(webEvent);
            this.resetCalculatedState();
        }
        if (!prevOverflowState && isOverflowed) {
            // if previously not overflowed and now overflowed, force a rebuild so web
            // consumers will be notified of this update
            restoreCursor(() => {
                this.textAsset.requiresRedraw = true;
            });
        }
    }

    /**
     * Resets the manging text asset's state as well as optionally clears its text
     */
    removeEmptyState(resetText: boolean = false) {
        if (resetText) {
            this.textAsset.text = '';
        }
        this.#editingEmptyState = false;
        this.textAsset.alpha = 1;
    }

    /**
     * Performs any cleanup necessary for when the manager is no longer needed, and disables the update loop
     */
    destroy() {
        this.#destroyed = true;
        this.#onResizeCallbacks = [];
        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
            this.#resizeObserver = null;
        }
        this.rootContainer.remove();
        if (this.isShowingEmptyState) {
            const resetText = true;
            this.removeEmptyState(resetText);
        }
        this._eventListenersToCleanup.forEach(([target, eventName, callback]) => {
            target.removeEventListener(eventName, callback);
        });
    }

    /**
     * Determines the html node and character offset to position the cursor given the current
     * state of the text input
     *
     * @param x x-axis click coord, relative to luminary's resolution
     * @param y y-axis click coord, relative to luminary's resolution
     */
    calculateCursorPos(x: number, y: number): NodeAndOffset {
        if (this.destroyed) {
            throw new Error('No cursor position can be provided for a destroyed text input.');
        }

        if (this.#editingEmptyState) {
            return {
                node: this.placeholderContainer,
                offset: 0,
            };
        }

        const localPoint = new PIXI.Point(x, y);
        const index = this.textAsset.getClosestTextIndex(localPoint);

        if (index === 0) {
            return {
                node: this.textInput.querySelector(`.${CanvasTextEditingManager.wordElementClass}`).firstChild,
                offset: 0,
            };
        }
        else {
            const charRange = [index, index] as CharTuple;
            const { anchorNode, anchorOffset } = getSelectionFromCharRange(charRange);
            return {
                node: anchorNode,
                offset: anchorOffset,
            };
        }
    }

    /**
     * Updates the inOverflowState property given a text value
     */
    handleOverflowState(text: string) {
        let canTextFit = this.textAsset.canFitText(text);

        // slight change to existing textSize to make sure and use it for max value to make sure getFontSizeToFitAllText return different size for textSize
        const MIN_SIZE_SHIFT = 5;
        const minSize = this.textAsset._textEditingOptions.minSize;

        if (!canTextFit && this.textAsset.textSize > minSize && this.textAsset.textSize - MIN_SIZE_SHIFT >= minSize) {
            const { baseFontSize, displaysAllText } = this.textAsset.getFontSizeToFitAllText({
                max: this.textAsset.textSize - MIN_SIZE_SHIFT,
                min: minSize,
            }, text);
            if (displaysAllText) {
                this.#inputEventData.newTextSize = baseFontSize;
            }
            canTextFit = displaysAllText;
        }
        this.inOverflowState = !canTextFit;
    }

    /**
     * Clears any calculated data from internal private members
     */
    resetCalculatedState() {
        this.#inputEventData.originalText = null;
        this.#inputEventData.updatedText = null;
        this.#inputEventData.newData = null;
        this.#inputEventData.netCharDiff = null;
        this.#inputEventData.prevCharRange = null;
        this.#inputEventData.eventType = null;
        this.#inputEventData.newTextSize = null;
    }

    /**
     * Updates any dynamic CSS styles based off of information stored in the managing
     * textAsset.
     */
    _updateStyles() {
        this._recalculateBounds();

        // Update positioning and bounds
        const bounds = this.textAsset.getBoundsOfTextWithoutGraphics();

        const resolution = this.textAsset.root.referenceResolution;
        this.rootContainer.style.position = 'absolute';
        this.rootContainer.style.left = `${bounds.x / resolution.width * 100}%`;
        this.rootContainer.style.top = `${bounds.y / resolution.height * 100}%`;
        this.rootContainer.style.width = `${bounds.width / resolution.width * 100}%`;
        this.rootContainer.style.height = `${bounds.height / resolution.height * 100}%`;

        // Update RTL direction
        // Use Luminary's text direction instead of html[dir] as the source of truth
        const appliedDirection = this.#textInput.getAttribute('dir') || Config.LEFT_TO_RIGHT_TEXT_DIRECTION;
        const globalTextDirection = this.textAsset.root.textDirection;
        if (appliedDirection !== globalTextDirection) {
            this.#textInput.setAttribute('dir', globalTextDirection);
        }
        const domScale = getRootContainerScale(this.textAsset.root, this.#domContainer.clientWidth);

        // Update font styles
        if (this.textAsset.hasText) {
            this.rootContainer.style.fontSize = `${this.textAsset.normalStyle.fontSize * domScale}px`;
        }

        // Update special empty state styling
        if (this.#editingEmptyState) {
            const placeholderNode = this.placeholderContainer;
            if (!placeholderNode.style.lineHeight) {
                const height = bounds.height;
                placeholderNode.style.lineHeight = `${domScale * height}px`;
                placeholderNode.style.height = `${domScale * height}px`;
                placeholderNode.style.top = `0px`;
            }
        }
    }

    /**
     * Creates and positions nodes for each wordElement stored in the managing
     * text asset as children of the text input
     *
     * Calling this method will clear all current children of the text input and will
     * momentarily lose the current cursor position, but place it back after the contents
     * have been rebuilt.
     */
    _buildContents() {
        this.#onResizeCallbacks = [];
        if (!this.textAsset.text.length && !this.#editingEmptyState) {
            this._showEmptyState();
            return;
        }
        if (this.#editingEmptyState) {
            return;
        }
        const stopBlurPropagation = e => e.stopPropagation();
        this.textInput.addEventListener('blur', stopBlurPropagation);
        this.textInput.textContent = '';
        this._recalculateBounds();
        buildInputContent(this).forEach(textLine => {
            this.textInput.appendChild(textLine);
        });
        this.textInput.removeEventListener('blur', stopBlurPropagation);
        this._repositionCursor();
    }

    /**
     * Modifies the current selection to place the cursor at the end of the
     * range representing the the last change.
     */
    _repositionCursor() {
        const { netCharDiff, prevCharRange, eventType, newData } = this.#inputEventData;

        // do not reposition if invoked without a change
        if (eventType === null) {
            return;
        }

        // record scenario where repositioning is invoked without valid data. Most likely an issue with
        // the current font not supporting the current value
        if (prevCharRange === null || netCharDiff === null || newData === null) {
            const err = new GroupedTextError(
                'The cursor cannot be repositioned without valid inputEventData',
                'RepositionCursorMisfireError',
                null
            );
            this._reportError(err, 'error', {
                ...this.#inputEventData,
            });
            return;
        }

        const isSelectionCollapsed = prevCharRange[0] === prevCharRange[1];

        // Calculate new cursor position
        let newOffset;
        if (eventType.endsWith('Forward')) {
            // In cases like pressing the Del key, the cursor should remain the same position
            newOffset = prevCharRange[0];
        }
        else {
            // Default case: place cursor after any additive changes
            newOffset = isSelectionCollapsed
                ? prevCharRange[0] + netCharDiff
                : prevCharRange[0] + newData.length;
        }

        // if new offset is out of bounds, report the error and set it back to a valid index
        if (newOffset < 0 || newOffset > this.textAsset.text.length) {
            const errMsg = 'The calculated offset was out of bounds.';
            const errName = this.#inputEventData.eventType === 'deleteContentBackward'
                ? 'deleteContentBackwardError'
                : 'OutOfBoundsOffsetError';
            const err = new GroupedTextError(errMsg, errName, null);
            const errMetadata = {
                newOffset,
                currTextValue: this.textAsset.text,
                ...this.#inputEventData,
            };
            const resetState = false;
            this._reportError(err, 'error', errMetadata, resetState);
            newOffset = newOffset < 0 ? 0 : this.textAsset.text.length;
        }

        try {
            const { anchorNode, anchorOffset } = getSelectionFromCharRange([newOffset, newOffset]);
            window.getSelection().collapse(anchorNode, anchorOffset);
        }
        catch (e) {
            const err = new GroupedTextError(e.message, 'CursorRepositionError', e);
            this._reportError(err, 'error', {
                newOffset,
                ...this.#inputEventData,
            });
        }

        this.resetCalculatedState();
    }

    /**
     * Before a change (via an InputEvent or Clipboard event from a paste) is made, cache
     * information about the incoming change that will be used to update text, cursor position,
     * and highlights.
     *
     * This method also determines if the incoming change can be fully displayed. If not, it sets
     * the parent text asset's inOverflowState property.
     */
    _preprocessChangeEvent(e: ClipboardEvent|InputEvent, newData: string) {
        this.resetCalculatedState();

        const isChangeDeletion = e.type === 'beforeinput'
            && (e as InputEvent).inputType.startsWith('delete');
        this.#inputEventData.eventType = e.type === 'beforeinput'
            ? (e as InputEvent).inputType
            : 'insertFromPaste';
        this.#inputEventData.newData = newData;
        this.#inputEventData.originalText = this.textAsset.text;

        // record cursor position before change is made
        if (this.#editingEmptyState) {
            this.#inputEventData.prevCharRange = [0, 0];
        }
        else {
            const readjustLineBreaks = isChangeDeletion;
            try {
                this.#inputEventData.prevCharRange = getCharRangeFromSelection(readjustLineBreaks);
            }
            catch (e) {
                const err = new GroupedTextError(e.message, 'CharRangeFromSelectionError', e);
                this._reportError(err, 'error', {
                    ...e.customData,
                    inputType: this.#inputEventData.eventType,
                    currText: this.textAsset.text,
                });
                throw err;
            }
        }

        // construct and test new text value if change is additive
        if (!isChangeDeletion) {
            let originalText = this.#editingEmptyState ? '' : this.textAsset.text;
            if (newData === '\n' && !originalText.endsWith('\n')) {
                // edge case: if user adds a trailing newline and there's not already one, insert an additional
                // one because they most likely wanted to immediately start editing a new line
                originalText = originalText + '\n';
            }
            const updatedText = originalText.slice(0, this.#inputEventData.prevCharRange[0])
                + this.#inputEventData.newData
                + originalText.slice(this.#inputEventData.prevCharRange[1]);

            this.#inputEventData.updatedText = updatedText;
            this.#inputEventData.netCharDiff = updatedText.length - originalText.length;

            this.handleOverflowState(this.#inputEventData.updatedText);
        }
        else {
            this.inOverflowState = false;
        }
    }

    /**
     * Determine if any rich text is pasted. If so, intercept event and sanitize it first,
     * then manually trigger the input handler
     */
    _handlePaste(e: ClipboardEvent) {
        const hasRichText = e.clipboardData.getData('text/html').length > 0;
        if (!hasRichText) {
            return;
        }

        // Manually insert sanitized plain text in case of pasted rich text
        e.preventDefault();

        // Ensure text is not overflowed before making change
        const plainText = e.clipboardData.getData('text/plain');
        const sanitizedText = sanitizeText(plainText);
        this._preprocessChangeEvent(e, sanitizedText);
        if (this.inOverflowState) {
            return;
        }

        // Simulate an input event with the sanitized data.
        // Note: there's no need to actually modify the DOM due to preprocessChangeEvent
        //  calculating the effects of the change.
        const pasteData = new DataTransfer();
        pasteData.setData('text/plain', sanitizedText);
        const inputEvent = new InputEvent('input', {
            inputType: 'insertFromPaste',
            data: '',
            dataTransfer: pasteData,
        });
        this._handleInput(inputEvent);
    }

    /**
     * Determines if the input event should be cancelled, and also precomputes information
     * from the incoming change to be used later in reconstructing text/cursor position
     */
    _handleBeforeInput(e: InputEvent) {
        // prevent any formatting changes from taking place (e.g. bold, italic, etc.)
        if (e.inputType.startsWith('format') || e.inputType.startsWith('history')) {
            e.preventDefault();
            return;
        }

        // determine what new additive change is made, if any
        const isInputTypeNewline = e.inputType === 'insertParagraph' || e.inputType === 'insertLineBreak';
        const isInputTypePaste = e.inputType.startsWith('insertFromPaste');
        let sanitizedData;
        if (isInputTypeNewline) {
            sanitizedData = '\n';
        }
        else if (isInputTypePaste) {
            // Ignore TS error: https://developer.mozilla.org/en-US/docs/Web/API/InputEvent/dataTransfer
            sanitizedData = sanitizeText(e.dataTransfer.getData('text/plain'));
        }
        else {
            sanitizedData = sanitizeText(e.data || '');
        }

        // prevent any changes that do not produce net-new text when in empty state
        if (this.#editingEmptyState && !sanitizedData.trim().length) {
            e.preventDefault();
            return;
        }

        // Process and store event data and check if the change can fit
        this._preprocessChangeEvent(e, sanitizedData);
        if (this.inOverflowState) {
            e.preventDefault();
        }
    }

    /**
     * On any change, sanitize the final state and assign it to the managing Text Asset's
     * text property. Then, calculate and store information needed to place the cursor back in its
     * expected position after the text input has been rebuilt
     */
    _handleInput(e: InputEvent) {
        const inputType = e.inputType;

        // It is expected that the beforeinput handler fired before this and inputEventData has been populated.
        // If not, check for edge cases and handle each case separately, returning early
        const isUnexpectedEvent = this.#inputEventData.eventType === null;
        if (isUnexpectedEvent) {
            // Grammarly correction
            const isGrammarlyPresent = document.querySelector(`#${CanvasTextEditingManager.rootContainerId} grammarly-extension`) !== null;
            const isTextInsertion = inputType === 'insertText';
            if (isGrammarlyPresent && isTextInsertion) {
                this._processGrammarlyEvent(e);
                if (this.inOverflowState) {
                    // rebuild contents since Grammarly changed it
                    this.#inputEventData.netCharDiff = 0;
                    this.#inputEventData.newData = '';
                    this.#inputEventData.updatedText = this.textAsset.text;
                    this._build();
                    return;
                }
            }
            else if (this.#editingEmptyState) {
                // Safari backspace in empty state deletes the placeholder node
                const isEventDeletion = inputType.startsWith('delete');
                const doesPlaceholderExist = document.querySelector(`#${CanvasTextEditingManager.placeholderId}`) !== null;
                if (isEventDeletion && !doesPlaceholderExist) {
                    this._showEmptyState();
                    this.resetCalculatedState();
                    return;
                }
            }
            else {
                // Unknown reason - we cannot process the input event. Report the error and group it if the
                // context is known
                let errorClass = 'HandleInputError';
                let errorMsg = 'The input handler was fired without a beforeinput event';
                if (inputType === 'insertCompositionText') {
                    errorClass = 'InsertCompositionTextError';
                    errorMsg = 'A text composition event was not properly handled';
                }
                const bugsnagErr = new GroupedTextError(errorMsg, errorClass, null);
                this._reportError(bugsnagErr, 'error', {
                    ...this.#inputEventData,
                    'firedInputEventType': inputType,
                    'eventData': e.data,
                });
                return;
            }
        }

        // Get the final text value to assign to the text asset. If not calculated before (e.g. deletion),
        // get the value straight from the contenteditable
        const originalText = this.#editingEmptyState ? '' : this.textAsset.text;
        let updatedText;
        if (this.#inputEventData.updatedText !== null) {
            updatedText = this.#inputEventData.updatedText;
        }
        else {
            updatedText = getContentFromInput();
            let netCharDiff = updatedText.length - originalText.length;

            // Edge case: if just the trailing newline was deleted and the previous selection was collapsed,
            // then delete the last character since the user probably meant to do that
            if (updatedText.length >= 1 &&
                netCharDiff === -1 &&
                updatedText[updatedText.length - 1] !== '\n' &&
                originalText[originalText.length - 1] === '\n'
            ) {
                updatedText = updatedText.slice(0, updatedText.length - 1);
                netCharDiff--;
            }

            // Edge case: when previous selection was collapsed, and it was in the middle of the text
            // but original text has \n and updated text does not, so we did not set correct netCharDiff
            if (updatedText[updatedText.length - 1] !== '\n' &&
                originalText[originalText.length - 1] === '\n' &&
                this.#inputEventData.prevCharRange[0] === this.#inputEventData.prevCharRange[1] &&
                this.#inputEventData.prevCharRange[0] < originalText.length
            ) {
                netCharDiff++;
            }

            this.#inputEventData.updatedText = updatedText;
            this.#inputEventData.netCharDiff = netCharDiff;
        }

        // revert back to special empty state if trimmed content is empty
        if (updatedText.trim().length === 0 && !this.#editingEmptyState) {
            this._showEmptyState();
            return;
        }

        // calculate new highlight data, if necessary
        let updatedHighlights;
        let areHighlightsOudated = this.textAsset.textHighlights.length && originalText.length !== updatedText.length;
        if (areHighlightsOudated) {
            const wasPrevSelectionCollapsed = this.#inputEventData.prevCharRange[0] ===
                this.#inputEventData.prevCharRange[1];
            const wasChangeDeletion = this.#inputEventData.eventType.startsWith('delete');
            let changeStartIdx = this.#inputEventData.prevCharRange[0];
            if (wasChangeDeletion && wasPrevSelectionCollapsed) {
                changeStartIdx += this.#inputEventData.netCharDiff;
            }

            const originalHighlights = this.textAsset.textHighlights.map(highlight => highlight.slice());
            updatedHighlights = getUpdatedHighlightRanges(
                this.#inputEventData.netCharDiff,
                changeStartIdx,
                originalHighlights
            );
            areHighlightsOudated = updatedHighlights !== this.textAsset.textHighlights;
        }

        this._batchDraws(() => {
            this.inOverflowState = false;

            // If previously empty and a change adds content, do not show empty state anymore
            if (this.#editingEmptyState && updatedText.length > 0) {
                this.removeEmptyState();
            }

            if (this.textAsset.text === updatedText) {
                // Redraw in case we need to remove enojis or other sanitized glyphs
                this.textAsset.requiresRedraw = true;
            }
            else {
                this.textAsset.text = updatedText;
                if (this.#inputEventData.newTextSize !== null) {
                    this.textAsset.textSize = this.#inputEventData.newTextSize;
                }
            }

            // Recalulate highlights, if necessary
            if (areHighlightsOudated && updatedHighlights) {
                this.textAsset.textHighlights = updatedHighlights;
            }
        });
    }

    /**
     * Generic top level handler for keydown events on the contenteditable node
     */
    _handleKeydown(e: KeyboardEvent) {
        const modifierKeyPressed = e.ctrlKey || e.altKey || e.shiftKey;
        const verticalArrowKeyPressed = e.key === 'ArrowDown' || e.key === 'ArrowUp';

        if (!modifierKeyPressed && verticalArrowKeyPressed && !this.isShowingEmptyState) {
            const { node, offset } = getSelectionForVerticalTraverse(
                e.key as 'ArrowDown' | 'ArrowUp',
                this.textAsset.textLinesForEditing,
                this.textAsset.text
            );
            e.preventDefault();
            window.getSelection().collapse(node, offset);
        }
    }

    /**
     * Runs logic that updates the managing text asset such that it is only build once,
     * after the update is finished
     *
     * @param updateFct a synchronous function
     */
    _batchDraws(updateFct: Function) {
        this.textAsset.autoDraw = false;
        updateFct();
        this.textAsset.autoDraw = true;
    }

    /**
     * Manually calculate the bounds of the parent text asset so all children's bounds
     * are up to date before any build logic.
     */
    _recalculateBounds() {
        if (!this.#isBoundsUpdatedForBuild) {
            const skipUpdate = false;
            this.textAsset.getBounds(skipUpdate);
            this.#isBoundsUpdatedForBuild = true;
        }
    }

    /**
    * Creates an empty structure containing the root container and a contenteditable node
    * with all listeners attached
    */
    _constructRootContainer(): HTMLDivElement {
        const rootContainer = document.createElement('div');
        // NTE text should not be translated.
        // This is essential so that the text remains aligned
        // and equal to the text in Luminary.
        rootContainer.setAttribute('translate', 'no');
        rootContainer.id = CanvasTextEditingManager.rootContainerId;

        const inputNode = createTextInput();

        // If a handler for any listened event fails, make sure we do any cleanup if needed
        // For every listener, overwrite the implicit `this` to be the TextEditingManager instance
        const wrapAndSetListener = (event, listener) => {
            const boundListener = listener.bind(this);
            const wrappedListener = (e: Event) => {
                try {
                    boundListener(e);
                }
                catch (err) {
                    this.resetCalculatedState();
                    throw err;
                }
            };
            const finalListener = wrappedListener.bind(this);
            inputNode.addEventListener(event, finalListener);
            this._eventListenersToCleanup.push([inputNode, event, finalListener]);
        };
        wrapAndSetListener('beforeinput', this._handleBeforeInput);
        wrapAndSetListener('paste', this._handlePaste);
        wrapAndSetListener('input', this._handleInput);
        wrapAndSetListener('keydown', this._handleKeydown);

        if (!this.#resizeObserver) {
            this.#resetOnDomResizedPromise();
            // Resize NTE elements when DOM container is resized
            this.#resizeObserver = new ResizeObserver((entries) => {
                const element = entries[0];
                this._callOnResizeCallbacks(element.contentRect.width);
                this.#onDomResizedResolve();
                this.#resetOnDomResizedPromise();
            });
            // Observe resize.
            this.#resizeObserver.observe(this.#domContainer);
        }

        rootContainer.appendChild(inputNode);
        this.#domContainer.appendChild(rootContainer);
        return rootContainer;
    }

    #resetOnDomResizedPromise() {
        this.#onDomResized = new Promise(resolve => {
            this.#onDomResizedResolve = resolve;
        });
    }

    /**
     * @internal
     */
    async _onDomResized(): Promise<void> {
        await this.#onDomResized;
    }

    /**
     * @internal
     */
    _addOnResizeCallback(callback: Function) {
        this.#onResizeCallbacks.push(callback);
    }

    /**
     * @internal
     */
    _callOnResizeCallbacks(containerWidth: number) {
        this.#onResizeCallbacks.map((callback: Function) => callback(containerWidth));
    }

    /**
     * Updates the children of the contenteditable as well as the state of the managing text asset
     * to give the illusion that the text asset is empty, while still maintaining a non-zero height
     * for the text asset
     */
    _showEmptyState() {
        this.#editingEmptyState = true;
        const invisiblePlaceholderContent = 'I'; // char with full ascent, gives rough height estimate for text asset

        // Create an HTML node that has non-zero bounds, styled like the text the user
        // will see once that start typing
        const caretColor = int2rgb(this.textAsset.normalStyle.fill);
        const placeholderNode = createTextPlaceholder(caretColor);
        this.textInput.textContent = '';
        this.textInput.appendChild(placeholderNode);

        // Make sure no highlights are set
        if (this.textAsset.textHighlights.length) {
            this.textAsset.textHighlights = [];
        }

        // Sync luminary state, making sure the text is invisible
        this.textAsset.text = invisiblePlaceholderContent;
        this.textAsset.alpha = 0;
        this._recalculateBounds();

        // Force cursor into the placeholder node since not all browsers will do this
        const selection = window.getSelection();
        const anchor = selection.anchorNode as HTMLElement;
        if (!anchor || anchor.id !== CanvasTextEditingManager.placeholderId) {
            const offset = 0;
            selection.collapse(placeholderNode, offset);
        }
    }

    /**
     * Takes an input event dispatched by Grammarly and populates internal inputEventData similar
     * to the beforeInput handler
     */
    _processGrammarlyEvent(e: InputEvent) {
        const sanitizedData = sanitizeText(e.data);
        // assumed selection is collapsed after grammarly forcibly makes the change
        const readjustLineBreaks = true;
        const grammarlyCharRange = getCharRangeFromSelection(readjustLineBreaks);
        // original is position before all the changes
        const originalIdx = grammarlyCharRange[0] - e.data.length;
        const originalCharRange = [originalIdx, originalIdx];

        const fullText = getContentFromInput();
        const sanitizedFullText = fullText.slice(0, originalIdx)
            + sanitizedData
            + fullText.slice(grammarlyCharRange[0], fullText.length);

        this.#inputEventData.eventType = e.inputType;
        this.#inputEventData.prevCharRange = originalCharRange as CharTuple;
        this.#inputEventData.newData = sanitizedData;
        this.#inputEventData.originalText = this.textAsset.text;
        this.#inputEventData.updatedText = sanitizedFullText;
        this.#inputEventData.netCharDiff = sanitizedData.length;

        this.handleOverflowState(sanitizedFullText);
    }

    /**
     * Ensure that whenever we need to notify Bugsnag, we also reset any calculated state information
     * as it is most likely the code will need to return early
     */
    _reportError(
        e: Error,
        severity: string,
        metadata?: object,
        resetState: Boolean = true
    ) {
        notifyBugsnag(e, severity, metadata);
        if (resetState) {
            this.resetCalculatedState();
        }
    }
}
